import React from 'react';
import { Box, Typography, Divider, useTheme, Alert } from '@mui/material';
import SlopeSummarySection from './SlopeSummarySection';
import { useAuth } from '../../context/AuthContext';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

const SlopeDashboard = () => {
  // 🔐 GET USER CONTEXT FOR PERMISSION FILTERING
  const { user, getUserOrganization } = useAuth();
  const theme = useTheme();
 
  // Get organization info from auth context
  const userOrganization = getUserOrganization();
  const organizationName = userOrganization?.name || '';

  // Check if user has organization-wide access
  const hasOrgWideAccess = user?.permissionScope !== 'network_section';

  return (
    <DisciplineAccessControl requiredDiscipline="Geotechnical">
    <Box sx={{ maxWidth: '100%' }}>
      {/* Slope Summary Section */}
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          fontWeight: 600,
          fontSize: { xs: '1.25rem', md: '1.5rem' }
        }}
      >
        Slope Infrastructure Overview
      </Typography>

      {hasOrgWideAccess ? (
        <SlopeSummarySection />
      ) : (
        <Box
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            textAlign: 'center',
            backgroundColor: 'background.paper',
            border: '2px dashed',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Access Restricted
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
            Slope dashboard requires organization-wide access permissions.
            Please contact your administrator to upgrade your access level.
          </Typography>
        </Box>
      )}

      {/* Future sections placeholder */}
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 2,
          mt: 4,
          fontWeight: 600,
          fontSize: { xs: '1.25rem', md: '1.5rem' }
        }}
      >
        Slope Analytics & Monitoring
      </Typography>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
        gap: 3,
        mb: 3
      }}>
        {/* Future: Slope Health Monitoring */}
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: '2px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: 'background.paper'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Slope Health Monitoring
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Real-time structural health indicators,
              <br />
              load capacity tracking, and
              <br />
              safety compliance monitoring
            </Typography>
          </Box>
        </Box>
        
        {/* Future: Inspection & Maintenance Timeline */}
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: '2px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: 'background.paper'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Inspection & Maintenance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Scheduled inspection timelines,
              <br />
              maintenance activity tracking, and
              <br />
              compliance reporting
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Add more sections here later */}
    </Box>
    </DisciplineAccessControl>
  );
};

export default SlopeDashboard;